import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import { FieldWorkerAnimatedLayout } from './components/FieldWorkerAnimatedLayout';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { useAuthStore } from './store/authStore';
import { LoginPage } from './components/LoginPage';
import { AdminEntryPointPicker } from './components/AdminEntryPointPicker';
import { AndroidBackButtonHandler } from './components/AndroidBackButtonHandler';
import { useDirection } from './hooks/useDirection';
import { usePushNotifications } from './hooks/usePushNotifications';
import { ErrorBoundary } from './components/ErrorBoundary';
import { getStoredAdminInterface, setStoredAdminInterface } from './utils/adminInterfaceSelection';

// Lazily loaded: keeps Firebase / Firestore and heavy dashboards out of the login-screen bundle (fixes iOS WebView "frozen" until JS finishes parsing).
const DocumentViewer = lazy(() =>
  import('./components/DocumentViewer').then((m) => ({ default: m.DocumentViewer })),
);
const ContractorDashboard = lazy(() =>
  import('./pages/ContractorDashboard').then((m) => ({ default: m.ContractorDashboard })),
);
const ContractorEmployeePage = lazy(() =>
  import('./pages/ContractorEmployeePage').then((m) => ({ default: m.ContractorEmployeePage })),
);
const ContractorSupportPage = lazy(() =>
  import('./pages/ContractorSupportPage').then((m) => ({ default: m.ContractorSupportPage })),
);
const ContractorGuidesPage = lazy(() =>
  import('./pages/ContractorGuidesPage').then((m) => ({ default: m.ContractorGuidesPage })),
);
const EmployeeDashboard = lazy(() =>
  import('./pages/EmployeeDashboard').then((m) => ({ default: m.EmployeeDashboard })),
);
const EmployeeSupportPage = lazy(() =>
  import('./pages/EmployeeSupportPage').then((m) => ({ default: m.EmployeeSupportPage })),
);
const EmployeeGuidesPage = lazy(() =>
  import('./pages/EmployeeGuidesPage').then((m) => ({ default: m.EmployeeGuidesPage })),
);
const OnboardingModal = lazy(() =>
  import('./components/OnboardingModal').then((m) => ({ default: m.OnboardingModal })),
);

// Lazily loaded admin pages to reduce initial bundle size
const ContractorsPage = lazy(() =>
  import('./pages/ContractorsPage').then((m) => ({ default: m.ContractorsPage })),
);

const EmployeesPage = lazy(() =>
  import('./pages/EmployeesPage').then((m) => ({ default: m.EmployeesPage })),
);

const ArchivedEmployeesPage = lazy(() =>
  import('./pages/ArchivedEmployeesPage').then((m) => ({ default: m.ArchivedEmployeesPage })),
);

const ArchivedContractorsPage = lazy(() =>
  import('./pages/ArchivedContractorsPage').then((m) => ({ default: m.ArchivedContractorsPage })),
);

const ContractorPage = lazy(() =>
  import('./pages/ContractorPage').then((m) => ({ default: m.ContractorPage })),
);

const EmployeePage = lazy(() =>
  import('./pages/EmployeePage').then((m) => ({ default: m.EmployeePage })),
);

const EmployeeMainPage = lazy(() =>
  import('./pages/EmployeeMainPage').then((m) => ({ default: m.EmployeeMainPage })),
);

const AdminDashboard = lazy(() =>
  import('./pages/AdminDashboard').then((m) => ({ default: m.AdminDashboard })),
);
const FieldWorkerPage = lazy(() =>
  import('./pages/FieldWorkerPage').then((m) => ({ default: m.FieldWorkerPage })),
);
const ActiveEmployeesByContractorPage = lazy(() =>
  import('./pages/ActiveEmployeesByContractorPage').then((m) => ({ default: m.ActiveEmployeesByContractorPage })),
);
const ActiveEmployeesByContractorDetailPage = lazy(() =>
  import('./pages/ActiveEmployeesByContractorDetailPage').then((m) => ({ default: m.ActiveEmployeesByContractorDetailPage })),
);
const FieldWorkerAssignmentPage = lazy(() =>
  import('./pages/FieldWorkerAssignmentPage').then((m) => ({ default: m.FieldWorkerAssignmentPage })),
);
const FieldWorkerAccessPage = lazy(() =>
  import('./pages/FieldWorkerAccessPage').then((m) => ({ default: m.FieldWorkerAccessPage })),
);
const FieldWorkerGuidesPage = lazy(() =>
  import('./pages/FieldWorkerGuidesPage').then((m) => ({ default: m.FieldWorkerGuidesPage })),
);

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const jwt = useAuthStore((state) => state.jwt);
  const authUser = useAuthStore((state) => state.authUser);
  const isAdmin = useAuthStore((state) => state.isAdmin());
  const isContractor = useAuthStore((state) => state.isContractor());
  const isEmployee = useAuthStore((state) => state.isEmployee());

  // Public routes - accessible without authentication
  const isPublicDocumentRoute = location.pathname.startsWith('/documents/');
  const isFieldWorkerPath = location.pathname === '/field-worker' || location.pathname.startsWith('/field-worker/');
  const isOfficePath =
    location.pathname === '/contractors' ||
    location.pathname === '/employees' ||
    location.pathname === '/archived-employees' ||
    location.pathname === '/archived-contractors' ||
    location.pathname === '/admin' ||
    location.pathname.startsWith('/contractor/') ||
    location.pathname.startsWith('/employee/') ||
    location.pathname.startsWith('/employee-main/');
  const adminEntryPoint: 'selection' | 'office' | 'field-worker' = isFieldWorkerPath
    ? 'field-worker'
    : isOfficePath
      ? 'office'
      : 'selection';

  useEffect(() => {
    if (!jwt || !isAdmin) return;
    if (adminEntryPoint === 'office' || adminEntryPoint === 'field-worker') {
      setStoredAdminInterface(adminEntryPoint);
    }
  }, [jwt, isAdmin, adminEntryPoint]);
  
  // If not authenticated and not on public route, show login
  if (!jwt && !isPublicDocumentRoute) {
    return <LoginPage />;
  }

  // Admin logged in - show manager entry point before admin interface
  if (jwt && isAdmin && !isPublicDocumentRoute && adminEntryPoint === 'selection') {
    const storedAdminInterface = getStoredAdminInterface();
    if (storedAdminInterface === 'office') {
      return <Navigate to="/contractors" replace />;
    }
    if (storedAdminInterface === 'field-worker') {
      return <Navigate to="/field-worker" replace />;
    }

    return (
      <AdminEntryPointPicker
        onOfficeSelect={() => {
          setStoredAdminInterface('office');
          navigate('/contractors');
        }}
        onFieldWorkerSelect={() => {
          setStoredAdminInterface('field-worker');
          navigate('/field-worker');
        }}
      />
    );
  }

  // Admin interface
  if (jwt && isAdmin && !isPublicDocumentRoute && adminEntryPoint === 'office') {
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <Routes>
          <Route path="/" element={<Navigate to="/contractors" replace />} />
          <Route path="/contractors" element={<ContractorsPage />} />
          <Route path="/employees" element={<EmployeesPage />} />
          <Route path="/archived-employees" element={<ArchivedEmployeesPage />} />
          <Route path="/archived-contractors" element={<ArchivedContractorsPage />} />
          <Route path="/contractor/:contractorId" element={<ContractorPage />} />
          <Route path="/employee/:employeeId" element={<EmployeePage />} />
          <Route path="/employee-main/:employeeId" element={<EmployeeMainPage />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="*" element={<Navigate to="/contractors" replace />} />
        </Routes>
      </Suspense>
    );
  }

  if (jwt && isAdmin && !isPublicDocumentRoute && adminEntryPoint === 'field-worker') {
    return (
      <Routes>
        <Route path="/" element={<Navigate to="/field-worker" replace />} />
        <Route path="/field-worker" element={<FieldWorkerAnimatedLayout />}>
          <Route index element={<FieldWorkerPage />} />
          <Route path="active-employees" element={<ActiveEmployeesByContractorPage />} />
          <Route path="active-employees/:contractorId" element={<ActiveEmployeesByContractorDetailPage />} />
          <Route path="assignment" element={<FieldWorkerAssignmentPage />} />
          <Route path="access" element={<FieldWorkerAccessPage />} />
          <Route path="guides" element={<FieldWorkerGuidesPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/field-worker" replace />} />
      </Routes>
    );
  }

  return (
    <>
      <Suspense fallback={<div>Loading...</div>}>
        <Routes>
          {/* Public routes - accessible without authentication */}
          <Route path="/documents/:documentType" element={<DocumentViewer />} />
          
          {/* Contractor authenticated */}
          {jwt && isContractor && authUser && (
            <>
              <Route path="/" element={<Navigate to="/contractor-dashboard" replace />} />
              <Route path="/contractor-dashboard" element={<ContractorDashboard />} />
              <Route path="/contractor-employee/:employeeId" element={<ContractorEmployeePage />} />
              <Route path="/contractor-support" element={<ContractorSupportPage />} />
              <Route path="/contractor-guides" element={<ContractorGuidesPage />} />
            </>
          )}
          
          {/* Employee authenticated */}
          {jwt && isEmployee && authUser && (
            <>
              <Route path="/" element={<Navigate to="/employee-dashboard" replace />} />
              <Route path="/employee-dashboard" element={<EmployeeDashboard />} />
              <Route path="/employee-support" element={<EmployeeSupportPage />} />
              <Route path="/employee-guides" element={<EmployeeGuidesPage />} />
            </>
          )}

          {/* Fallback redirects */}
          {jwt && isContractor && authUser && (
            <Route path="*" element={<Navigate to="/contractor-dashboard" replace />} />
          )}
          {jwt && isEmployee && authUser && (
            <Route path="*" element={<Navigate to="/employee-dashboard" replace />} />
          )}
        </Routes>
      </Suspense>
      {/* Onboarding modal for contractors - not on public routes */}
      {jwt && isContractor && authUser && !isPublicDocumentRoute && (
        <Suspense fallback={null}>
          <OnboardingModal />
        </Suspense>
      )}
    </>
  );
}

function App() {
  useDirection();
  usePushNotifications();

  // Hide native splash only after React has painted — reduces "frozen white WebView" before UI is ready.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let cancelled = false;
    const run = async () => {
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
      );
      await new Promise((r) => setTimeout(r, 80));
      if (cancelled) return;
      await SplashScreen.hide({ fadeOutDuration: 220 }).catch(() => {});
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AndroidBackButtonHandler />
        <AppContent />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;

